import { Label } from '../../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { useAnalytics } from '../../../core/analytics/useAnalytics';
import type { SchemeId } from '../model/keybindingTypes';
import { availableKeybindingSchemes } from '../store/keybindingSchemes';
import { useKeybindingsStore } from '../store/keybindingsStore';

const schemeIds = Object.keys(availableKeybindingSchemes) as SchemeId[];

const isAvailableScheme = (value: string): value is SchemeId =>
  Object.prototype.hasOwnProperty.call(availableKeybindingSchemes, value);

/** "Jam SQL (default)", then the other tools' schemes under their own names. */
const schemeName = (id: SchemeId): string => {
  const label = availableKeybindingSchemes[id]?.label ?? id;
  return id === 'jam' ? `${label} (default)` : label;
};

/**
 * The shortcut scheme the overrides layer on (plan D2, D7). The list is the
 * store's scheme registry. Switching keeps the user's own shortcuts; the store
 * re-validates, and a kept shortcut that collides with the new scheme wins
 * with a notice (D6). `feature:keybinding_scheme_changed` fires only when the
 * scheme actually changed; both ids come from the closed scheme set.
 */
export function SchemePicker() {
  const { track } = useAnalytics();
  const scheme = useKeybindingsStore((state) => state.scheme);

  const changeScheme = (value: string) => {
    const from = useKeybindingsStore.getState().scheme;
    // The same two conditions under which the store's setScheme is a no-op.
    if (!isAvailableScheme(value) || value === from) return;
    useKeybindingsStore.getState().setScheme(value);
    track('feature:keybinding_scheme_changed', { from, to: value });
  };

  return (
    <div className="flex items-center gap-2">
      <Label htmlFor="keybinding-scheme" className="text-xs text-muted-foreground">
        Scheme
      </Label>
      <Select value={scheme} onValueChange={changeScheme}>
        <SelectTrigger
          id="keybinding-scheme"
          size="sm"
          className="w-[210px]"
          data-testid="keybinding-scheme-select"
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {schemeIds.map((id) => (
            <SelectItem key={id} value={id} data-testid={`keybinding-scheme-option-${id}`}>
              {schemeName(id)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
